import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, login as apiLogin, signup as apiSignup, LoginCredentials, SignupData } from '../api/auth'
import apiClient from '../api/client'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  signup: (data: SignupData) => Promise<void>
  logout: () => Promise<void>
  error: string | null
  clearError: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Token storage key
const TOKEN_KEY = 'access_token'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY)

      // Set token in axios header if available from localStorage
      if (token) {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      }

      // Apply theme from localStorage immediately (before auth check)
      const savedTheme = localStorage.getItem('theme')
      if (savedTheme) {
        document.documentElement.setAttribute('data-theme', savedTheme)
      }

      try {
        // Verify authentication by getting current user
        // This works for both localStorage token and HttpOnly cookie
        const response = await apiClient.post<User>('/login/test-token')
        setUser(response.data)

        // Apply theme from user profile if available
        if (response.data.theme) {
          document.documentElement.setAttribute('data-theme', response.data.theme)
          localStorage.setItem('theme', response.data.theme)
        }
      } catch {
        // Not authenticated or token invalid
        if (token) {
          localStorage.removeItem(TOKEN_KEY)
          delete apiClient.defaults.headers.common['Authorization']
        }
      }
      setIsLoading(false)
    }

    checkAuth()
  }, [])

  const login = async (credentials: LoginCredentials) => {
    setError(null)
    try {
      const response = await apiLogin(credentials)

      // Store token
      localStorage.setItem(TOKEN_KEY, response.access_token)
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.access_token}`

      // Get user info
      const userResponse = await apiClient.post<User>('/login/test-token')
      setUser(userResponse.data)

      // Apply theme from user profile immediately after login
      if (userResponse.data.theme) {
        document.documentElement.setAttribute('data-theme', userResponse.data.theme)
        localStorage.setItem('theme', userResponse.data.theme)
      }
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err)
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const signup = async (data: SignupData) => {
    setError(null)
    try {
      await apiSignup(data)
      // After signup, automatically login
      await login({ username: data.email, password: data.password })
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err)
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const logout = async () => {
    // Clear localStorage token
    localStorage.removeItem(TOKEN_KEY)
    delete apiClient.defaults.headers.common['Authorization']

    // Call logout endpoint to clear cookie
    try {
      await apiClient.post('/logout')
    } catch {
      // Ignore errors - we're logging out anyway
    }

    setUser(null)
  }

  const clearError = () => setError(null)

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        signup,
        logout,
        error,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Helper function to extract error message
function getErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const axiosError = err as { response?: { data?: { detail?: string } } }
    if (axiosError.response?.data?.detail) {
      const detail = axiosError.response.data.detail
      // Map API error codes to user-friendly messages
      switch (detail) {
        case 'incorrect_credentials':
          return 'Invalid email or password'
        case 'inactive_user':
          return 'Your account is inactive. Please contact support.'
        case 'user_already_exists':
          return 'An account with this email already exists'
        case 'registration_forbidden':
          return 'Registration is currently disabled'
        default:
          return detail
      }
    }
  }
  return 'An unexpected error occurred'
}
